import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { Modal } from '@els/els-react--modal';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Pill } from '@els/els-react--pill';
import useBreakpoint from '@Hooks/useBreakpoint';

import ThesaurusGroupsRoot from './ThesaurusGroups/ThesaurusGroupsRoot';
import SelectedSearchItems from './SelectedSearchItems';
import TreeViewDialogFooter from './TreeViewDialogFooter';
import { focusAfterClearAll } from './treeViewDialogFocus';

import './TreeViewDialog.scss';

function TreeViewDialog(props) {
  const { filter, selectedCount, selfClose, tags, expandSearchOptions, onToggleSearchOptions, onClickClearAllSelected } = props;
  const title = filter?.get('title');
  const isTabletUp = useBreakpoint('tablet');
  // Above mobile the selected-items panel is always shown, so the toggle
  // behavior (and its collapsed state) only applies on mobile.
  const selectedItemsExpanded = isTabletUp || Boolean(expandSearchOptions);

  const headingRef = useRef(null);
  const toggleFocusedRef = useRef(false);
  const wasTabletUpRef = useRef(isTabletUp);

  // Crossing into tablet+ (resize/rotate) unmounts the mobile toggle button in
  // favor of plain text. If it held focus, that focus would otherwise drop to
  // <body> — move it to the heading instead. Checking document.activeElement
  // here would race the unmount (jsdom/browsers clear it back to <body> as
  // soon as the focused node leaves the DOM, before this effect runs), so
  // focus state is captured via onFocus/onBlur instead.
  useEffect(() => {
    if (isTabletUp && !wasTabletUpRef.current && toggleFocusedRef.current) {
      headingRef.current?.focus();
    }
    wasTabletUpRef.current = isTabletUp;
  }, [isTabletUp]);

  // Clearing empties the pill list, so there is no sibling left to receive
  // focus — hand it to the tree, which is where the user picks terms again.
  const handleClearAllSelected = () => {
    onClickClearAllSelected();
    focusAfterClearAll();
  };

  return filter ? (
    <Modal
      onClose={selfClose}
      shouldCloseOnEscape
      shouldPreventClickOverlayToClose
      shouldHideCloseButton
      className="react-dialogs-scrollable treeViewDialog"
      id="tree-view-dialog"
      label={title || 'Filter lookup'}>
      <div className="treeViewDialog__wrapper">
        <div className="treeViewDialog__header">
          <h3 className="treeViewDialog__header_title" data-e2e="treeViewDialog-title">
            {title}
          </h3>
          <ButtonWithIcon
            data-e2e="treeViewDialog-header-close"
            type="link"
            size="small"
            sprite="Close"
            linkIconSize="xs"
            iconOnly
            className="u-els-margin-right-1o2"
            onClick={selfClose}
            aria-label="Close filter lookup">
            Close
          </ButtonWithIcon>
        </div>

        <div className="treeViewDialog__body u-els-margin-bottom">
          <div className="treeViewDialog__tree">
            <ThesaurusGroupsRoot {...props} />
          </div>

          <section className="treeViewDialog__selected" aria-labelledby="treeViewDialog-selected-heading">
            <div className="treeViewDialog__selectedHeading">
              <Pill className="treeViewDialog__selectedCount" data-testid="treeViewDialog-selected-count">
                <span aria-hidden="true">{String(selectedCount ?? 0)}</span>
                <span className="u-els-hide-visually">{`${selectedCount ?? 0} selected`}</span>
              </Pill>
              <h4
                id="treeViewDialog-selected-heading"
                ref={headingRef}
                tabIndex={-1}
                className="u-els-font-size-base u-els-line-height-default">
                {isTabletUp ? (
                  <span data-e2e="selected-items-heading-text">Selected search items</span>
                ) : (
                  <ButtonWithIcon
                    id="treeViewDialog-selected-items-toggle"
                    data-e2e="selected-items-toggle"
                    type="borderless"
                    size="small"
                    sprite={selectedItemsExpanded ? 'ChevronUp' : 'ChevronDown'}
                    iconRight
                    onClick={onToggleSearchOptions}
                    onFocus={() => {
                      toggleFocusedRef.current = true;
                    }}
                    onBlur={() => {
                      toggleFocusedRef.current = false;
                    }}
                    aria-expanded={selectedItemsExpanded}
                    aria-controls="treeViewDialog-selected-items">
                    Selected search items
                  </ButtonWithIcon>
                )}
              </h4>
              {onClickClearAllSelected && (
                <Button
                  className="treeViewDialog__clearSelected"
                  data-e2e="clear-selected"
                  type="link"
                  size="small"
                  onClick={handleClearAllSelected}>
                  Clear selected
                </Button>
              )}
            </div>
            <div id="treeViewDialog-selected-items" className="treeViewDialog__selectedItems" hidden={!selectedItemsExpanded}>
              <SelectedSearchItems {...props} />
            </div>
          </section>
        </div>

        <div className="treeViewDialog__footer">
          <TreeViewDialogFooter {...props} tags={tags} />
        </div>
      </div>
    </Modal>
  ) : null;
}

TreeViewDialog.propTypes = {
  filter: PropTypes.instanceOf(Map),
  selectedCount: PropTypes.number,
  expandSearchOptions: PropTypes.bool,
  tags: PropTypes.instanceOf(List),
  selfClose: PropTypes.func.isRequired,
  onClickClearAllSelected: PropTypes.func,
  onToggleSearchOptions: PropTypes.func.isRequired,
  onClickThesaurusItem: PropTypes.func.isRequired,
};

export default TreeViewDialog;
